import type { CSSProperties, ReactNode } from 'react'

import {
  getContrastTextColor,
  isValidHexColor,
  type WeddingThemeColors,
} from '@/lib/theme'

interface WeddingThemeProviderProps {
  theme?: WeddingThemeColors | null
  className?: string
  style?: CSSProperties
  children: ReactNode
}

/**
 * Scopes CSS custom-property overrides to its subtree (never
 * document.documentElement) so a wedding's colors never leak outside the
 * guest/display surfaces this wraps. Overriding --primary/--primary-foreground
 * works because theme.css's `@theme inline` block declares
 * `--color-primary: var(--primary)` as a live reference — every existing
 * bg-primary/text-primary/border-primary utility inside this subtree
 * re-resolves against the override automatically.
 */
export function WeddingThemeProvider({
  theme,
  className,
  style,
  children,
}: WeddingThemeProviderProps) {
  const vars: CSSProperties = {}

  if (isValidHexColor(theme?.primaryColor)) {
    Object.assign(vars, {
      '--primary': theme.primaryColor,
      '--primary-foreground': getContrastTextColor(theme.primaryColor),
    })
  }

  if (isValidHexColor(theme?.secondaryColor)) {
    Object.assign(vars, {
      '--wedding-secondary': theme.secondaryColor,
      '--wedding-secondary-foreground': getContrastTextColor(
        theme.secondaryColor,
      ),
    })
  }

  return (
    <div className={className} style={{ ...vars, ...style }}>
      {children}
    </div>
  )
}
